import React from 'react';
import { Save, ChevronRight, ChevronLeft } from 'lucide-react';
import { TabType } from '../types';
import { cn } from '@/lib/utils';
import { useTranslation, useRTL } from '../utils/translations';
import { formatPrice, isCarPriceVisible } from '../utils/pricing';

interface MobileFooterProps {
  price: number;
  activeTab: TabType;
  onNext: () => void;
  isPreviewMode: boolean;
  isDarkMode: boolean;
  onSaveClick?: () => void;
  nextTabLabel?: string;
}

export const MobileFooter: React.FC<MobileFooterProps> = ({ price, activeTab, onNext, isPreviewMode, isDarkMode, onSaveClick, nextTabLabel }) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  
  if (isPreviewMode) return null;

  const getButtonText = () => {
    if (nextTabLabel) return nextTabLabel;
    if (activeTab === 'CONFIG') return t('staticTranslations.common.next', 'Next');
    if (activeTab === 'MODEL') return t('staticTranslations.common.exterior', 'Exterior');
    if (activeTab === 'EXTERIOR') return t('staticTranslations.common.interior', 'Interior');
    if (activeTab === 'INTERIOR') return t('staticTranslations.common.options', 'Options');
    if (activeTab === 'OPTIONS') return t('staticTranslations.common.summary', 'Summary');
    return t('staticTranslations.common.proceed', 'Proceed');
  };

  return (
    <div className={cn(
      "md:hidden sticky bottom-0 z-50 border-t h-[46px] px-[14px] py-[3px] flex items-center justify-between transition-colors duration-300",
      isDarkMode ? "bg-[#0a0a0a] border-white/5" : "bg-white border-[#e6e6e6]"
    )}>
      {isCarPriceVisible() ? (
      <div className="flex flex-col gap-[2px]">
        <span className={cn(
          'font-sans text-[9px] font-normal uppercase tracking-tight whitespace-nowrap',
          isDarkMode ? "text-white/40" : "text-[#666]"
        )}>{t('staticTranslations.common.startingPrice', 'Starting Price')}</span>
        <span className={cn(
          'font-sans text-[17px] font-bold leading-none',
          isDarkMode ? "text-white" : "text-[#333334]"
        )}>{formatPrice(price)}</span>
      </div>
      ) : <div />}
      
      <div className="flex items-center gap-[11px]">
        <button className={cn(
          'h-[32px] px-3 rounded-[var(--brand-button-radius-sm)] text-[12px] font-bold font-sans flex items-center justify-center gap-[8px] transition-colors whitespace-nowrap border',
          isDarkMode ? "border-white/30 text-white bg-transparent hover:bg-white/10" : "border-[var(--brand-accent)] text-[var(--brand-accent)] bg-transparent hover:bg-blue-50"
        )}
        onClick={onSaveClick}>
          {t('staticTranslations.common.saveYourBuild', 'Save Your Build')}
          <Save className="w-[12px] h-[12px] flex-shrink-0" />
        </button>
        <button 
          onClick={onNext}
          className='h-[32px] px-4 bg-[var(--brand-accent)] text-white rounded-[var(--brand-button-radius-sm)] text-[12px] font-bold font-sans flex items-center justify-center gap-[6px] hover:bg-[var(--brand-accent-hover)] transition-colors whitespace-nowrap min-w-[90px]'
        >
          {getButtonText()}
          {isRTL ? (
            <ChevronLeft className="w-[12px] h-[12px] stroke-[2.5] flex-shrink-0" />
          ) : (
            <ChevronRight className="w-[12px] h-[12px] stroke-[2.5] flex-shrink-0" />
          )}
        </button>
      </div>
    </div>
  );
};
